import Auth from '../services/Auth';

// How a sign-in method is named to the user
const METHOD_NAMES = {
  'apple.com': 'Apple',
  'facebook.com': 'Facebook',
  'google.com': 'Google',
};
// What a password sign-in gets for an email whose account has no password,
// as well as for a wrong password
const PASSWORD_REFUSED = ['auth/invalid-credential', 'auth/wrong-password'];
const OTHER_CREDENTIAL = 'auth/account-exists-with-different-credential';

/**
 * The message for a sign-in refused because the email already has an account
 * with another method, naming that method, as the apps do: the user signs in
 * with it or resets the password. Under "link accounts", a social sign-in over
 * another method fails with `account-exists-with-different-credential`; a
 * password sign-in on an account with no password fails like a wrong password.
 * @param {Function} t the translate function
 * @param {Error} error what Firebase threw
 * @param {string} [email] the email typed, for a password sign-in
 * @returns {Promise<string|null>} the message, or null when the error is
 *   something else (a wrong password on a password account included)
 */
export async function accountExistsMessage(t, error, email) {
  const code = error?.code;
  const otherCredential = code === OTHER_CREDENTIAL;
  if (!otherCredential && !PASSWORD_REFUSED.includes(code)) return null;
  const address = (otherCredential && error?.customData?.email) || email;
  let methods = [];
  if (address) {
    try {
      methods = await Auth.fetchSignInMethodsForEmail(Auth.instance, address);
    } catch {
      methods = [];
    }
  }
  if (!otherCredential && (!methods.length || methods.includes('password'))) {
    return null;
  }
  const names = methods.map((method) =>
    method === 'password' ? t('auth_method_password') : METHOD_NAMES[method]
  );
  if (!names.length || names.some((name) => !name)) {
    return t('auth_error_account_exists_other_method_unknown');
  }
  return t('auth_error_account_exists_other_method', {
    methods: names.join(', '),
  });
}

export default accountExistsMessage;
